import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit } from "@angular/core";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MapService } from "../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";
import { ConnectedPosition } from "@angular/cdk/overlay";
import type { AgentDefinition } from "../../../ai-components/chat/model/agent.types";
import { AiChatSessionService } from "../../../ai-components/chat/services/ai-chat-session.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-collection-controller",
  templateUrl: "./collection-controller.component.html",
  styleUrls: ["./collection-controller.component.scss"],
  standalone: false,
})
export class CollectionControllerComponent implements OnInit {
  public viewerService = inject(VIEWER_SERVICE);

  FeatureFlags = FeatureFlags;

  private _orientation: string;

  private _mode: any;

  enableObliqueMode: boolean; // the selected collection have disabled the oblique mode

  enableObliqueByZoom: boolean; // the selected collection have disabled the oblique mode by the zoom level

  private _centerAOI: IProjectAOI;

  private _aois: IProjectAOI[];

  private _imageAOIs: IProjectAOI[];

  controllerRotation = 0;

  textRotation = 0;

  lockController = true;

  lockRotation = false;

  showInfraredButton3D = false;

  enableInfraredButton = false;

  is3D: boolean = false;

  @Input()
  isBaseMapActivated: boolean = false;

  @Input() agents: AgentDefinition[] = [];

  chatPanelOpen = false;

  readonly chatPanelPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "end",
      overlayY: "bottom",
    },
  ];

  private readonly chatSession = inject(AiChatSessionService);

  readonly currentThreadId = this.chatSession.threadId;

  readonly selectedAgentId = this.chatSession.agentId;

  readonly activeAgent = computed(() => this.agents.find((a) => a.id === this.chatSession.agentId()) ?? null);

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected flagService: FeatureFlagsService,
  ) {}

  ngOnInit(): void {
    if (this.agents.length >= 1 && this.chatSession.agentId() === null) {
      this.chatSession.agentId.set(this.agents[0].id);
    }
  }

  toggleChatPanel(): void {
    this.chatPanelOpen = !this.chatPanelOpen;
  }

  onAgentChange(agentId: string): void {
    this.chatSession.switchAgent(agentId);
  }

  onChatNewChat(): void {
    this.chatSession.startNewChat();
  }

  @Input()
  set aois(aois: IProjectAOI[]) {
    this._aois = aois;
  }

  get aois(): IProjectAOI[] {
    return this._aois;
  }

  @Input()
  set imageAOIs(aois: IProjectAOI[]) {
    this._imageAOIs = aois;
  }

  get imageAOIs(): IProjectAOI[] {
    return this._imageAOIs;
  }

  @Input()
  set centerAOI(aoi: IProjectAOI) {
    this._centerAOI = aoi;
    this.updateObliqueEnabled();
  }

  get centerAOI(): IProjectAOI {
    return this._centerAOI;
  }

  @Input()
  set mode(mode: any) {
    this._mode = mode;
    this.is3D = mode.name === ViewerConstants.COLLECTION_MODES.OBLIQUE.name;
  }

  get mode(): any {
    return this._mode;
  }

  @Input()
  set zoom(event: any) {
    if (event !== null) {
      this.enableObliqueByZoom = event.zoom >= 19;
    }
  }

  @Input()
  set initialized(initialized: boolean) {
    this.lockController = !initialized;
    this.updateInfraredStatus();
  }

  @Input()
  set orientation(orientation: string) {
    this.updateControlOrientation(this.orientation, orientation);
    this._orientation = orientation;
    this.updateMeasureOrientation(orientation);
  }

  @Input()
  controllerBlocked = false;

  get orientation(): string {
    return this._orientation;
  }

  changeMode() {
    if (!this.isEnabled()) {
      return;
    }
    this.viewerService.changeMode();
    this.updateInfraredStatus();
  }

  changeOrientation(orientation: string) {
    if (this.lockRotation || !this.orientation || !this.isEnabled() || !this.availableOrientation(orientation)) {
      return;
    }
    this.viewerService.changeOrientation(orientation);

    // eslint-disable-next-line no-param-reassign
    orientation =
      orientation == ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]
        ? ViewerConstants.DISCRETE_ORIENTATIONS.V
        : orientation;

    this.updateInfraredStatus();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CHANGE_ORIENTATION + orientation,
      GoogleAnalyticsConstantsCategory.COLLECTION_CONTROLLER,
      true,
    );
  }

  checkOrientationNorthOrSouth(orientation: string) {
    return (
      orientation === ViewerConstants.DISCRETE_ORIENTATIONS.N || orientation === ViewerConstants.DISCRETE_ORIENTATIONS.S
    );
  }

  isEnabled() {
    return !this.lockController && this.mode && this.orientation;
  }

  /**
   * Enable 3D button
   */
  enable3DButton(): boolean {
    return (
      !this.lockController &&
      this.enableObliqueMode &&
      this.enableObliqueByZoom &&
      this.have3DOrientation() &&
      !this.isBaseMapActivated
    );
  }

  haveObliqueImagery(): boolean {
    return this.centerAOI?.hasObliques() || false;
  }

  have3DOrientation(): boolean {
    return this.centerAOI?.hasNonArchivedOrientation(this.orientation) || false;
  }

  updateObliqueEnabled() {
    // find image aoi (with imagery coverage) and check if its enabled
    let enable = false;
    if (this.centerAOI) {
      const imageAOI = this.imageAOIs.find((aoi) => aoi.getLayerId() === this.centerAOI.getLayerId());
      if (imageAOI) {
        enable = imageAOI.isEnabled(); /// image aois always have oblique enabled
      } else {
        enable = this.centerAOI.isEnabled3D() && this.centerAOI.isEnabled();
      }
    }
    this.enableObliqueMode = enable;
    this.updateInfraredStatus();
  }

  availableOrientation(orientation: string): boolean {
    if (this.mode.name === ViewerConstants.COLLECTION_MODES.VERTICAL.name) {
      return true;
    }
    return this.centerAOI?.hasNonArchivedOrientation(orientation) || false;
  }

  updateInfraredStatus() {
    const cirAvailable = this.centerAOI?.isCirAvailable(this.is3D);
    this.showInfraredButton3D = !this.lockController && this.enableObliqueMode && this.is3D;
    this.enableInfraredButton =
      (this.viewerService.getZoom() >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL && cirAvailable) ||
      (this.viewerService.getZoom() < ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL &&
        !this.viewerService.getInfraredStatus());
  }

  /**
   * Enable 3D button
   */
  enableNadirButton(): boolean {
    return !this.lockController && this.enableObliqueMode && this.is3D && this.haveNadirOrientation();
  }

  haveNadirOrientation(): boolean {
    return this.centerAOI?.hasNonArchivedOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]) || false;
  }

  haveInfraredOrientation(): boolean {
    return !!this.centerAOI?.orientations.some((orientation) =>
      ViewerConstants.DISCRETE_ORIENTATIONS.I.includes(orientation),
    );
  }

  loadNadirImage(): void {
    if (this.orientation !== ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]) {
      this.changeOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]);
    }
  }

  loadInfraredImage(): void {
    if (this.orientation !== ViewerConstants.DISCRETE_ORIENTATIONS.I) {
      this.changeOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.I);
    }
  }

  get3DModeMessage(): string {
    if (this.enable3DButton() || this.viewerService.is3DMode()) {
      return null;
    }
    if (this.lockController) {
      return "Loading layers...";
    }
    if (!this.enableObliqueByZoom) {
      return `Zoom in to access 3D imagery.`;
    }
    if (this.isBaseMapActivated) {
      return "3D mode is not available when the basemap is active.";
    }
    if (!this.enableObliqueMode || !this.haveObliqueImagery()) {
      return `A 2D collection is selected on the timeline. 3D imagery is not available.`;
    }
    if (!this.have3DOrientation()) {
      return `The ${
        ViewerConstants.COLLECTION_MODES.OBLIQUE.orientations[this.orientation].description
      }-facing view is not available in 3D mode here.`;
    }
    return null;
  }

  transitionRotationEnd() {
    this.lockRotation = false;
  }

  transitionRotationStart() {
    this.lockRotation = true;
  }

  updateControlOrientation(old: string, orientation: string) {
    if (!this.orientation) {
      return;
    }
    let newOrientation = orientation;
    let currentOrientation = old;
    const compassPositions = ["N", "E", "S", "W"];
    const northOrientation = compassPositions[0];

    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(currentOrientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === currentOrientation
    ) {
      currentOrientation = northOrientation;
    }
    // fix to force north position when we are in nadir to avoid glitches
    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(orientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === orientation
    ) {
      newOrientation = northOrientation;
    }
    const currentPosition = compassPositions.indexOf(currentOrientation);
    const newPosition = compassPositions.indexOf(newOrientation);
    switch (currentPosition - newPosition) {
      case 0:
        break;
      case 1:
      case -3:
        this.controllerRotation += 90;
        this.textRotation -= 90;
        break;
      case 2:
      case -2:
        this.controllerRotation -= 180;
        this.textRotation += 180;
        break;
      case 3:
      case -1:
        this.controllerRotation -= 90;
        this.textRotation += 90;
        break;
      default:
        break;
    }
  }

  updateMeasureOrientation(orientation: string) {
    const measureToolTips = Array.from(document.getElementsByClassName(ViewerConstants.MEASURE_TOOLTIPS));
    if (!this.orientation || measureToolTips.length === 0) {
      return;
    }
    const currentOrientation = {
      isNorthOrSouth: this.checkOrientationNorthOrSouth(this.orientation),
    };
    const newOrientation = {
      isNorthOrSouth: this.checkOrientationNorthOrSouth(orientation),
    };

    measureToolTips.forEach((element: any) => {
      if (currentOrientation.isNorthOrSouth !== newOrientation.isNorthOrSouth) {
        let rotation = parseFloat(
          element.style.transform.replace(/[^-?\d.]*/g, ""), // gets all numbers from a string, with negative sign if necessary
        );
        if (rotation > 0) {
          rotation -= 90;
        } else {
          rotation += 90;
        }
        element.style.transform = `rotate(${rotation}deg)`;
      }
    });
  }
}
